import React, { useRef, useCallback } from 'react';
import type { ModalProps, MantineSize } from '@mantine/core';
import { ModalController as ModalControllerClass } from '@ayocore/platform/client/components/ModalHost/ModalController';

/** Конфиг для useModal.open() — стандартные ModalProps плюс кастомные опции платформы */
export type ModalConfig = Omit<ModalProps, 'opened' | 'onClose' | 'children' | 'title'> & {
  /** Визуальный стиль фона модалки. По умолчанию — белый. */
  background?: 'default' | 'glassmorphism';
  /** Размер инпутов внутри модалки. Применяется через вложенный MantineProvider. По умолчанию — 'xs'. */
  inputSize?: MantineSize;
  /**
   * Показывать кнопку «Свернуть» рядом с крестиком. Свёрнутая модалка прячется вместе
   * с бекдропом в док внизу экрана (MinimizedDock); клик по чипу разворачивает её обратно.
   * Состояние формы при сворачивании сохраняется. По умолчанию — true;
   * отключается явным minimizable: false (например, для confirm-диалогов).
   */
  minimizable?: boolean;
  /**
   * Показывать кнопку «На весь экран» в шапке. Переключает модалку между обычным
   * размером и fullScreen. Начальное состояние берётся из fullScreen (см. ModalProps).
   * По умолчанию — true; отключается явным fullScreenable: false.
   */
  fullScreenable?: boolean;
  /**
   * Запретить закрытие по клику на бекдроп и по Esc — чтобы случайный клик мимо
   * не потерял несохранённый ввод формы. Крестик в шапке остаётся, осознанно закрыть можно.
   * Эквивалентно closeOnClickOutside: false + closeOnEscape: false.
   * По умолчанию — false.
   */
  preventClose?: boolean;
};

/**
 * Контроллер модалки, возвращаемый хуком useModal.
 * Предоставляет методы для открытия, закрытия и обработчика закрытия модалки.
 */
export interface ModalController<P extends Record<string, unknown>> {
  /**
   * Открывает модалку с переданными пропсами.
   * @param props - Пропсы компонента модалки (типизированы автоматически). Может включать `title?: string` для заголовка модалки (будет извлечен и не передан в компонент)
   * @param config - Опции для настройки Modal (size, centered и т.д.). Использует ModalProps из Mantine (без title, так как он в props)
   * @returns ID открытой модалки
   */
  open: (props: P & { title?: string }, config?: ModalConfig) => string;

  /**
   * Закрывает текущую открытую модалку.
   * Если модалка не открыта, ничего не происходит.
   */
  close: () => void;

  /**
   * Устанавливает обработчик закрытия модалки.
   * Callback будет вызван при закрытии модалки (через close() или пользователем).
   * Повторный вызов перезаписывает предыдущий обработчик.
   * @param callback - Функция, которая будет вызвана при закрытии модалки
   */
  onClose: (callback: () => void) => void;
}

/**
 * useModal — хук для работы с модальными окнами Mantine через декларативный DSL.
 *
 * Скрывает всю инфраструктуру модалок (ModalHost, стеки, провайдеры) за простым контроллером.
 * Разработчик видит только компонент модалки и методы open/close/onClose.
 *
 * @param Component - React-компонент модалки. Компонент должен принимать пропсы типа P
 *                    и возвращать только содержимое (без обёртки в <Modal>, так как Modal создаётся автоматически)
 * @returns Контроллер модалки с методами open, close и onClose
 *
 * @example
 * ```tsx
 * // Компонент содержимого модалки (без обёртки в Modal)
 * interface UserEditModalProps {
 *   userId: number;
 *   userName: string;
 * }
 *
 * function UserEditModalContent({ userId, userName }: UserEditModalProps) {
 *   return (
 *     <Stack>
 *       <Text>Редактирование пользователя {userName} (ID: {userId})</Text>
 *       <Button>Сохранить</Button>
 *     </Stack>
 *   );
 * }
 *
 * // Использование хука
 * function MyComponent() {
 *   const userModal = useModal(UserEditModalContent);
 *
 *   const handleEdit = () => {
 *     userModal.open(
 *       { userId: 42, userName: 'Иван' },
 *       { title: 'Редактировать пользователя', size: 'lg' }
 *     );
 *   };
 *
 *   userModal.onClose(() => {
 *     console.log('Модалка закрыта');
 *   });
 *
 *   return <Button onClick={handleEdit}>Редактировать</Button>;
 * }
 * ```
 *
 * @remarks
 * - Хук использует собственный ModalController с ModalContainer для управления модалками
 * - Поддерживается стек модалок (можно открывать несколько одновременно)
 * - Состояние компонентов сохраняется при открытии вложенных модалок (keepMounted)
 * - Типобезопасность: TypeScript автоматически выводит типы пропсов из компонента
 * - Modal создаётся автоматически, компонент должен возвращать только содержимое
 */
export function useModal<P extends Record<string, unknown>>(
  Component: React.ComponentType<P>
): ModalController<P> {
  const modalIdRef = useRef<string | null>(null);
  const onCloseCallbackRef = useRef<(() => void) | null>(null);
  const controller = ModalControllerClass.getInstance();

  // Единая точка закрытия: синхронизируем локальный id и вызываем актуальный callback.
  const handleModalClosed = useCallback(() => {
    modalIdRef.current = null;
    onCloseCallbackRef.current?.();
  }, []);

  /**
   * Открывает модалку с переданными пропсами
   */
  const open = useCallback(
    (props: P & { title?: string }, config?: ModalConfig): string => {
      // Извлекаем title из props (если есть) и убираем его из пропсов компонента
      const { title, ...componentProps } = props;

      // Если модалка уже открыта, обновляем её
      // Иначе создаем новую с уникальным ID
      const id =
        modalIdRef.current || `modal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      modalIdRef.current = id;

      // Открываем или обновляем модалку через контроллер.
      // Коллбек закрытия читается из ref, поэтому не накапливается между ререндерами.
      controller.open(
        id,
        <Component {...(componentProps as P)} />,
        {
          size: 'lg', // Дефолтное значение
          ...(title && { title }), // Добавляем title, если он передан в props
          ...config, // Переданные опции перезаписывают дефолты
        },
        [handleModalClosed]
      );

      return id;
    },
    [Component, controller, handleModalClosed]
  );

  /**
   * Закрывает текущую открытую модалку
   */
  const close = useCallback(() => {
    const modalId = modalIdRef.current;
    if (modalId) {
      controller.close(modalId);
      // Дублируем сброс локального id на случай, если modal уже закрыт в контроллере.
      modalIdRef.current = null;
    }
  }, [controller]);

  /**
   * Устанавливает обработчик события закрытия модалки
   */
  const onClose = useCallback((callback: () => void) => {
    onCloseCallbackRef.current = callback;
  }, []);

  return {
    open,
    close,
    onClose,
  };
}
